import { useState } from "react";
import { Trans } from "react-i18next";
import { IconLinePlus, IconLineEdit, IconLineLink } from "@kalodata/kds-design-icons";
import { useAppDispatch } from "@hooks/redux";
import { CreateProductEntityModal } from "@pages/library/CreateProductEntityModal";
import type { CreateProductMode } from "@pages/library/CreateProductEntityModal/types";
import { fetchProductList } from "@pages/library/librarySlice";
import { CreateEntityMethodDropdown } from "./CreateEntityMethodDropdown";
import type { ClipDropdownOption } from "@components/ClipDropdown/ClipDropdown";

const ICON_CLASS = "shrink-0 text-kds-fg-soft";

const OPTIONS: ClipDropdownOption[] = [
  {
    key: "link",
    label: (
      <div className="flex items-center gap-[8px]">
        <IconLineLink size={16} className={ICON_CLASS} />
        <Trans>Product Link</Trans>
      </div>
    ),
  },
  {
    key: "manual",
    label: (
      <div className="flex items-center gap-[8px]">
        <IconLineEdit size={16} className={ICON_CLASS} />
        <Trans>Create Manually</Trans>
      </div>
    ),
  },
];

export interface CreateProductEntityCardProps {
  /** 创建成功后刷新 Created 列表（由 UICardWrap 注入） */
  onCreated?: () => void;
}

/**
 * Library > Product > Created 占位卡片。
 *
 * 点击卡片内容区（+ 图标 + 文字）弹出 Creation Method 下拉，用户选 link / manual / saved
 * 后再以对应模式打开 CreateProductEntityModal。
 *
 * 卡片外层负责样式（边框/背景/尺寸/hover），内层内容区作为 dropdown trigger，
 * 使 dropdown 锚定在内容区下方，与卡片底部重叠展示（匹配 Figma 设计）。
 */
export const CreateProductEntityCard = ({ onCreated }: CreateProductEntityCardProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [defaultMode, setDefaultMode] = useState<CreateProductMode>("link");
  const dispatch = useAppDispatch();

  const handlePick = (mode: string) => {
    setDefaultMode(mode as CreateProductMode);
    setModalOpen(true);
  };

  const handleSuccess = () => {
    onCreated?.();
    // Saved 列表也可能用到新建后的商品，捎带刷下收藏列表
    dispatch(fetchProductList());
  };

  return (
    <>
      <div className="group bg-kds-bg border border-kds-border rounded-[8px] min-h-[327px] flex flex-col cursor-pointer hover:bg-kds-bg-element transition-colors">
        <div className="flex-1 w-full shrink-0 flex items-center justify-center">
          <CreateEntityMethodDropdown options={OPTIONS} onChange={handlePick}>
            <div className="flex flex-col items-center gap-[8px]">
              <div className="bg-kds-bg-subtle group-hover:border-kds-accent rounded-full size-[48px] flex items-center justify-center transition-colors border border-kds-bg-subtle">
                <IconLinePlus size={20} className="text-kds-fg" />
              </div>
              <span className="text-[14px] text-kds-fg">
                <Trans>Create Product Entity</Trans>
              </span>
            </div>
          </CreateEntityMethodDropdown>
        </div>
      </div>

      <CreateProductEntityModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleSuccess}
        defaultMode={defaultMode}
      />
    </>
  );
};
